import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { ArrowLeft, TrendingUp, Award, AlertTriangle, Receipt } from "lucide-react";

const COLORS = ["#0d1628", "#b89b6f", "#52525b", "#8b5cf6", "#10b981", "#ef4444"];
const STATUS_COLORS = {
    active: "#10b981",
    expired: "#ef4444",
    cancelled: "#52525b",
    pending: "#b89b6f",
};
const STATUS_LABELS = {
    active: "Ativa",
    expired: "Expirada",
    cancelled: "Cancelada",
    pending: "Pendente",
};

export default function AdminDashboardPage() {
    const [data, setData] = useState(null);
    const [months, setMonths] = useState(12);

    useEffect(() => {
        api.get(`/admin/revenue/summary?months=${months}`)
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)));
    }, [months]);

    const statusData = useMemo(() => {
        if (!data?.status_counts) return [];
        return Object.entries(data.status_counts).map(([k, v]) => ({
            name: STATUS_LABELS[k] || k,
            value: v,
            color: STATUS_COLORS[k] || "#52525b",
        }));
    }, [data]);

    if (!data) return <div className="label-caps" data-testid="dashboard-loading">A carregar dashboard...</div>;

    const { totals, mrr_series, plan_distribution, top_companies, alerts = [] } = data;

    return (
        <div className="space-y-10" data-testid="admin-dashboard-page">
            {alerts.length > 0 && (
                <div className="space-y-2" data-testid="alerts-banner">
                    {alerts.map((a) => (
                        <Link
                            key={a.id}
                            to={a.link || "#"}
                            className={`block border-l-4 p-4 ${a.severity === "critical" ? "border-[#dc2626] bg-[#fef2f2]" : "border-[#b89b6f] bg-[#fdf8f0]"}`}
                            data-testid={`alert-${a.id}`}
                        >
                            <div className="flex items-start gap-3">
                                <AlertTriangle size={18} className={a.severity === "critical" ? "text-[#dc2626] mt-0.5" : "text-[#b89b6f] mt-0.5"} />
                                <div className="flex-1">
                                    <div className={`font-medium ${a.severity === "critical" ? "text-[#7f1d1d]" : "text-[#0d1628]"}`}>{a.title}</div>
                                    <div className="text-sm text-[#52525b] mt-1">{a.message}</div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
            <div>
                <Link to="/app/super" className="label-caps inline-flex items-center gap-2" data-testid="back-to-super">
                    <ArrowLeft size={12} /> Super Admin
                </Link>
                <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
                    <div>
                        <h1 className="font-display text-5xl text-[#0d1628]">Dashboard de receita</h1>
                        <p className="text-[#52525b] mt-3 max-w-3xl">
                            Visão global de subscrições, receita acumulada e estado de faturação Moloni.
                        </p>
                    </div>
                    <div>
                        <label className="label-caps block mb-2">Período</label>
                        <select className="input-clean" value={months} onChange={(e) => setMonths(Number(e.target.value))} data-testid="period-select">
                            <option value={3}>3 meses</option>
                            <option value={6}>6 meses</option>
                            <option value={12}>12 meses</option>
                            <option value={24}>24 meses</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Kpi icon={TrendingUp} label="Receita total" value={`€${totals.total_revenue.toFixed(2)}`} />
                <Kpi icon={Receipt} label="Subscrições" value={totals.total_subscriptions} />
                <Kpi icon={Award} label="Faturadas" value={`${totals.total_invoiced} / ${totals.total_subscriptions}`} />
                <Kpi
                    icon={AlertTriangle}
                    label="Por faturar"
                    value={totals.pending_invoice}
                    accent={totals.pending_invoice > 0}
                />
            </div>

            {/* Revenue chart */}
            <section>
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp size={14} className="text-[#b89b6f]" />
                    <div className="label-caps">Receita mensal</div>
                </div>
                <div className="border border-[#e4e4e7] p-4 bg-white" style={{ height: 320 }} data-testid="mrr-chart">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={mrr_series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                            <XAxis dataKey="month" stroke="#52525b" style={{ fontSize: 11 }} />
                            <YAxis stroke="#52525b" style={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                            <Tooltip
                                cursor={{ fill: "rgba(184,155,111,0.08)" }}
                                contentStyle={{ background: "#0d1628", border: "none", color: "#fff", fontSize: 12 }}
                                formatter={(v) => [`€${Number(v).toFixed(2)}`, "Receita"]}
                            />
                            <Bar dataKey="revenue" fill="#0d1628" radius={[2, 2, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>

            <section className="grid lg:grid-cols-2 gap-10">
                {/* Status pie */}
                <div>
                    <div className="label-caps mb-4">Estado das subscrições</div>
                    <div className="border border-[#e4e4e7] p-4 bg-white" style={{ height: 300 }} data-testid="status-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={statusData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    label={({ name, value }) => `${name}: ${value}`}
                                >
                                    {statusData.map((entry) => <Cell key={`status-cell-${entry.name}`} fill={entry.color} />)}
                                </Pie>
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Plan distribution */}
                <div>
                    <div className="label-caps mb-4">Distribuição por plano</div>
                    <div className="border border-[#e4e4e7] p-4 bg-white" style={{ height: 300 }} data-testid="plan-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={plan_distribution} layout="vertical" margin={{ top: 10, right: 20, left: 60, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                <XAxis type="number" stroke="#52525b" style={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                                <YAxis type="category" dataKey="plan" stroke="#52525b" style={{ fontSize: 11 }} width={80} />
                                <Tooltip
                                    cursor={{ fill: "rgba(184,155,111,0.08)" }}
                                    contentStyle={{ background: "#0d1628", border: "none", color: "#fff", fontSize: 12 }}
                                    formatter={(v, k) => k === "revenue" ? [`€${Number(v).toFixed(2)}`, "Receita"] : [v, "Subs"]}
                                />
                                <Bar dataKey="revenue" fill="#b89b6f" radius={[0, 2, 2, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </section>

            {/* Top companies */}
            <section>
                <div className="label-caps mb-4">Top 5 empresas (receita acumulada)</div>
                <div className="border border-[#e4e4e7]" data-testid="top-companies">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-[#e4e4e7] bg-[#f5f3ee]">
                                <th className="text-left p-4 label-caps">#</th>
                                <th className="text-left p-4 label-caps">Empresa</th>
                                <th className="text-right p-4 label-caps">Subscrições</th>
                                <th className="text-right p-4 label-caps">Receita</th>
                                <th className="text-right p-4 label-caps">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {top_companies.length === 0 ? (
                                <tr><td className="p-6 text-center text-[#52525b]" colSpan={5}>Sem dados.</td></tr>
                            ) : top_companies.map((c, i) => (
                                <tr key={c.company_id} className="border-b border-[#e4e4e7] last:border-0" data-testid={`top-row-${i}`}>
                                    <td className="p-4 text-[#b89b6f] font-display text-2xl">{i + 1}</td>
                                    <td className="p-4 text-[#0d1628]">{c.company_name}</td>
                                    <td className="p-4 text-right text-[#52525b]">{c.count}</td>
                                    <td className="p-4 text-right text-[#0d1628] font-medium">€{c.revenue.toFixed(2)}</td>
                                    <td className="p-4 text-right">
                                        <Link to={`/app/super/companies/${c.company_id}`} className="btn-ghost text-xs">
                                            Ver
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Series line for trend */}
            {mrr_series.length > 1 && (
                <section>
                    <div className="label-caps mb-4">Evolução nº de subscrições / mês</div>
                    <div className="border border-[#e4e4e7] p-4 bg-white" style={{ height: 260 }} data-testid="count-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={mrr_series}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                                <XAxis dataKey="month" stroke="#52525b" style={{ fontSize: 11 }} />
                                <YAxis stroke="#52525b" style={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip contentStyle={{ background: "#0d1628", border: "none", color: "#fff", fontSize: 12 }} />
                                <Line type="monotone" dataKey="count" stroke="#b89b6f" strokeWidth={2} dot={{ fill: "#b89b6f", r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </section>
            )}
        </div>
    );
}

function Kpi({ icon: Icon, label, value, accent }) {
    return (
        <div className={`border ${accent ? "border-[#b89b6f]" : "border-[#e4e4e7]"} p-5`}>
            <div className="flex items-center gap-2 text-[#52525b]">
                <Icon size={14} className={accent ? "text-[#b89b6f]" : "text-[#b89b6f]"} />
                <div className="label-caps">{label}</div>
            </div>
            <div className={`mt-3 font-display text-3xl ${accent ? "text-[#b89b6f]" : "text-[#0d1628]"}`}>{value}</div>
        </div>
    );
}
